<template>
  <FusionBaseDialog
    :open="open"
    @close="close"
    @after-leave="reset"
    :title="$t('subscriptionBillingInfoDialog.title')"
    useXCloseButton
    icon="mdi-credit-card"
    persistentOverlay
    padded
    :no-actions="isBillingCreditCard"
    data-cy="subscriptionBillingInfoDialog"
  >
    <v-card-subtitle class="px-0 mb-4 text-wrap text-black">
      {{ $t('subscriptionBillingInfoDialog.subtitle') }}
    </v-card-subtitle>

    <v-radio-group
      v-model="paymentType"
      inline
      :disabled="shouldDisableSwitchingPaymentTypes"
      class="mx-n2"
    >
      <v-radio
        :label="$t('subscriptionBillingInfoDialog.cardBilling')"
        :value="paymentTypes.CC"
        color="primary"
        :data-cy="'paymentTypeParameterCC'"
      ></v-radio>
      <v-radio
        :label="$t('subscriptionBillingInfoDialog.purchaseOrderBilling')"
        :value="paymentTypes.PO"
        color="primary"
        :data-cy="'paymentTypeParameterPO'"
      ></v-radio>
    </v-radio-group>

    <div
      id="billing-info-monthly-purchase-order-issue"
      class="hint-text"
      v-if="shouldDisplayMonthlyPurchaseOrderIssue"
    >
      {{ $t('subscriptionBillingInfoDialog.purchaseOrderMonthlyInfo') }}
    </div>

    <v-text-field
      v-if="isBillingPurchaseOrder"
      v-model="purchaseOrderReference"
      :label="$t('subscriptionBillingInfoDialog.enterPurchaseOrder')"
      maxlength="35"
      counter="35"
      :rules="[rules.required]"
      :disabled="shouldDisablePurchaseOrderEntry"
      data-cy="billingInfoPurchaseOrderEntry"
      id="billing-info-purchase-order-entry"
      class="text-field billing-info-purchase-order-entry"
    ></v-text-field>

    <div
      id="delegoIFrameHost"
      v-show="shouldDisplayCreditCardIFrame"
      class="mb-2 delego-iframe-host"
    ></div>
    <div
      v-if="shouldDisplayCreditCardIFrameLoading"
      id="progressSpinner"
      class="text-center emptyMessage mb-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="36"
        class="mx-auto"
      ></v-progress-circular>
    </div>

    <div v-if="shouldDisplayTemporaryChargeInfo" class="mb-8">
      {{ $t('subscriptionBillingInfoDialog.temporaryChargeInfo') }}
    </div>

    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-progress-circular
        v-if="shouldDisplayBillingUpdateInProgress"
        indeterminate
        color="primary"
        size="36"
        class="mx-auto"
      ></v-progress-circular>
      <v-spacer></v-spacer>
      <v-btn
        v-if="shouldDisplayUpdateBillingButton"
        @click="updateBilling"
        class="bg-primary text-white"
        :disabled="shouldDisableUpdateBillingButton"
        data-cy="subscriptionBillingInfoUpdateBilling"
      >
        {{ $t('buttons.save') }}
      </v-btn>
    </template>
  </FusionBaseDialog>
</template>
<script lang="ts">
import { defineComponent } from 'vue'
import { FusionStores } from '@fusion/saas-components-v3'
import { HTTPStatus } from '@/api'
import { MessageType, SNACKBAR_STATIC_DURATION_MS } from '@/services/notifications'
import { NAMESPACES } from '@/store/types'
import { SUBSCRIPTIONS_ACTIONS } from '@/store/subscriptions'
import { monthlySubscriptions, paymentTypes } from '@/store/subscriptions/utils'

declare const DelegoRapidPay: {
  open: (options: Record<string, unknown>) => void
}

interface BillingDialogMessage {
  text: string
  type: MessageType
  timeout: number
}

interface CreditCardInfo {
  cardType: string
  cardToken: string
  cardExpiry: string
  cardHolderName: string
}

interface DelegoPayment {
  cardType: string
  token: string
  cardExpiration: {
    month: string
    year: string
  }
  cardholderName: string
}

interface DelegoTokenizeResponse {
  payment: DelegoPayment
}

interface DelegoTokenResponse {
  token: string
}

export default defineComponent({
  name: 'SubscriptionBillingInfoDialog',
  setup() {
    const notifications = FusionStores.notifications()
    return { notifications }
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    activationId: {
      type: String,
      default: ''
    },
    subscriptionId: {
      type: String,
      default: ''
    }
  },
  emits: ['close'],
  data() {
    return {
      paymentType: paymentTypes.CC as string,
      paymentTypes,
      creditCardInfo: null as CreditCardInfo | null,
      isDelegoLoaded: false,
      purchaseOrderReference: '',
      isUpdating: false,
      rules: {
        empty: (value: string | null): boolean => value === null || value.length === 0,
        required: (value: string): string | boolean => !!value || this.$t('validation.required')
      }
    }
  },
  computed: {
    isMonthly(): boolean {
      return (monthlySubscriptions as readonly string[]).includes(this.subscriptionId)
    },
    isBillingPurchaseOrder(): boolean {
      return this.paymentType === paymentTypes.PO
    },
    isBillingCreditCard(): boolean {
      return this.paymentType === paymentTypes.CC
    },
    fieldsValid(): boolean {
      if (this.isBillingPurchaseOrder) {
        return !this.isMonthly && this.rules.required(this.purchaseOrderReference) === true
      }
      if (this.isBillingCreditCard) {
        return this.creditCardInfo != null
      }
      return false
    },
    shouldDisableSwitchingPaymentTypes(): boolean {
      return this.isUpdating
    },
    shouldDisplayMonthlyPurchaseOrderIssue(): boolean {
      return this.isMonthly && this.isBillingPurchaseOrder
    },
    shouldDisablePurchaseOrderEntry(): boolean {
      return this.isMonthly || this.isUpdating
    },
    shouldDisplayCreditCardIFrame(): boolean {
      return this.isBillingCreditCard && this.isDelegoLoaded
    },
    shouldDisplayCreditCardIFrameLoading(): boolean {
      return !this.isDelegoLoaded && this.isBillingCreditCard
    },
    shouldDisplayBillingUpdateInProgress(): boolean {
      return this.isUpdating
    },
    shouldDisplayUpdateBillingButton(): boolean {
      return !this.isUpdating && (this.isBillingPurchaseOrder || this.creditCardInfo != null)
    },
    shouldDisableUpdateBillingButton(): boolean {
      return !this.fieldsValid
    },
    shouldDisplayTemporaryChargeInfo(): boolean {
      return this.isBillingCreditCard && this.creditCardInfo != null
    }
  },
  watch: {
    open(): void {
      if (this.open) {
        this.delegoInit().catch(() => {
          // nothing to do
        })
      }
    }
  },
  unmounted(): void {
    this.clearDelegoIFrame()
  },
  methods: {
    close(): void {
      if (this.isUpdating) {
        return
      }
      this.$emit('close')
    },
    reset(): void {
      this.clearFields()
      this.clearDelegoIFrame()
    },
    clearDelegoIFrame(): void {
      this.isDelegoLoaded = false
      const element = document.getElementById('delegoIFrameHost')
      if (element && element.firstChild) {
        element.removeChild(element.firstChild)
      }
    },
    clearFields(): void {
      this.creditCardInfo = null
      this.purchaseOrderReference = ''
      this.paymentType = paymentTypes.CC
    },
    async updateBilling(): Promise<void> {
      this.isUpdating = true
      const message = (await (this.isBillingPurchaseOrder
        ? this.updateBillingPurchaseOrder()
        : this.updateBillingCreditCard())) as BillingDialogMessage
      this.notifications.addMessage(message)
      this.isUpdating = false
      this.close()
    },
    async updateBillingPurchaseOrder(): Promise<BillingDialogMessage> {
      const eMsg: BillingDialogMessage = {
        text: this.$t('subscriptionBillingInfoDialog.toasts.purchaseOrderFailure'),
        type: MessageType.ERROR,
        timeout: SNACKBAR_STATIC_DURATION_MS
      }

      try {
        const resp = await this.$api.postBillingInfoPurchaseOrder(
          this.activationId,
          this.purchaseOrderReference
        )

        //not success
        if (resp.status !== HTTPStatus.OK) {
          return eMsg
        }

        //subscriptions need to be updated
        await this.updateSubscriptions()

        //success
        return {
          text: this.$t('subscriptionBillingInfoDialog.toasts.purchaseOrderConfirmation'),
          type: MessageType.SUCCESS,
          timeout: SNACKBAR_STATIC_DURATION_MS
        }
      } catch {
        //not success
        return eMsg
      }
    },
    async updateBillingCreditCard(): Promise<BillingDialogMessage> {
      const eMsg: BillingDialogMessage = {
        text: this.$t('subscriptionBillingInfoDialog.toasts.cardFailure'),
        type: MessageType.ERROR,
        timeout: SNACKBAR_STATIC_DURATION_MS
      }

      try {
        const resp = await this.$api.postBillingInfoCreditCard(
          this.activationId,
          this.creditCardInfo.cardType,
          this.creditCardInfo.cardToken,
          this.creditCardInfo.cardExpiry,
          this.creditCardInfo.cardHolderName
        )

        //not success
        if (resp.status !== HTTPStatus.OK) {
          return eMsg
        }

        //subscriptions need to be updated
        await this.updateSubscriptions()

        //success
        return {
          text: this.$t('subscriptionBillingInfoDialog.toasts.cardConfirmation'),
          type: MessageType.SUCCESS,
          timeout: SNACKBAR_STATIC_DURATION_MS
        }
      } catch {
        //not success
        return eMsg
      }
    },
    updateSubscriptions(): Promise<void> {
      return this.$store.dispatch(
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS}`,
        {
          auth: this.$auth,
          api: this.$api,
          devices: this.$auth.isAuthenticated ? this.$store.state.devices.allDevices : []
        }
      )
    },
    //START: DELEGO FUNCTIONS
    async delegoInit(): Promise<void> {
      this.creditCardInfo = null
      const response = await this.$api.getDelegoToken()
      const { token } = (await response.json()) as DelegoTokenResponse
      this.delegoOpenRapidPay(token)
    },
    delegoHasInit(): void {
      this.isDelegoLoaded = true
    },
    delegoSuccess(tokenizeResponse: DelegoTokenizeResponse): void {
      const payment = tokenizeResponse.payment
      const cardExpiry = payment.cardExpiration
      this.creditCardInfo = {
        cardType: payment.cardType,
        cardToken: payment.token,
        cardExpiry: `${cardExpiry.month}/${cardExpiry.year}`,
        cardHolderName: payment.cardholderName
      }
    },
    delegoFailure(): void {
      this.creditCardInfo = null
    },
    delegoChangePayment(): void {
      this.creditCardInfo = null
    },
    delegoOpenRapidPay(authorizationToken: string): void {
      const self = this
      DelegoRapidPay.open({
        url: APP_ENV.VITE_APP_DELEGO_URL,
        token: authorizationToken,
        paymentRequest: {
          currencyCode: 'USD',
          total: 0.01
          /*
           * Payment request object
           */
        },
        element: '#delegoIFrameHost',
        appclass: 'tsilink',
        events: {
          onApplicationInitialized: function () {
            /*
             * Actions to take once RapidPay is displayed
             */
            self.delegoHasInit()
          },
          onTokenizeSuccess: function (tokenizeResponse: DelegoTokenizeResponse) {
            /*
             * Actions to take once RapidPay has tokenized a payment card
             */
            self.delegoSuccess(tokenizeResponse)
          },
          onChangePaymentMethodClicked: function () {
            self.delegoChangePayment()
          },
          onFailure: function () {
            /*
             * Actions to take if RapidPay fails to tokenize a payment card (typically a system error)
             */
            self.delegoFailure()
          }
        }
        /*
         * Additional optional parameters, as desired.
         */
      })
      //END: DELEGO FUNCTIONS
    }
  }
})
</script>

<style lang="scss" scoped>
.delego-iframe-host {
  :deep(.delego-rapidpay) {
    width: 100%;
    height: 100%;
    border-width: 0;
  }
}

:deep(.v-selection-control--inline) {
  margin-right: 16px;
}

.billing-info-purchase-order-entry {
  padding-top: 6px;

  :deep(.v-label) {
    font-size: 14px;
    font-weight: 400;
  }

  :deep(.v-label.v-field-label--floating) {
    font-size: var(--v-field-label-scale);
  }

  :deep(.v-field) {
    font-size: 14px;
    font-weight: 400;
  }

  :deep(.v-field__input) {
    --v-field-input-padding-bottom: 2px;
  }

  :deep(.v-messages__message) {
    font-size: 10.5px;
    font-weight: 400;
  }
}
</style>
